import {
  Button, Card, Container, Select, TextInput,
} from 'nhsuk-react-components';
import {
  ChangeEvent, useEffect, useReducer, useRef, useState,
} from 'react';
import {
  Link, useParams,
} from 'react-router-dom';
import ruleReducer, {
  IRuleState,
  RuleActions,
} from 'reducers/rule/ruleReducer.tsx';
import {
  attributesHaveConditionType,
  buildInitialRuleState,
  getAttributeLevels,
} from 'reducers/rule/utils.ts';
import { IterationCohort } from 'schemas/IterationCohort.schema.ts';
import {
  IterationRule,
  RuleEditor,
  RuleType,
} from 'schemas/IterationRule.schema.ts';
import {
  validStringCharacterMessage,
  validStringCharacters,
} from 'schemas/shared-validators/regex.ts';
import { LibraryRuleMetadata } from 'schemas/LibraryRule.schema.ts';
import {
  attributeLevelToLabel, capitaliseFirstChar,
  conditionTargetToLabel,
  deriveDisplayName,
  normaliseCommaSeparatedListWithAttributeToIgnoreWhitelist,
} from 'utils/formattingUtils.ts';
import {
  buildRulePathParam,
  incrementAndClauseName,
} from 'utils/navigationUtils.ts';
import { getComparatorRequiredError } from 'utils/ruleEditorUtils.ts';
import { buildChangeLibraryRulePath, getNextRulePriorityForType } from 'utils/ruleUtils.ts';
import useCampaign from 'hooks/useCampaign.ts';
import useReferenceData from 'hooks/useReferenceData.ts';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import useIterationLock from 'hooks/useIterationLock.ts';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import EditorSelector from 'components/forms/Rule/Editors/EditorSelector.tsx';
import { ActionTypes } from 'components/forms/Rule/Editors/ActionTypes.ts';
import 'components/forms/Rule/Rule.scss';
import { IIterationLevelParams } from 'utils/parameterUtils';
import { getCohortStatus } from 'utils/cohortUtils.ts';

const NO_COHORTS_LABEL = 'Apply to all cohorts';

export type RuleFormValues = {
    Name: string;
    Description: string;
    Priority: number;
    Operator: string;
    Comparator?: string;
    AttributeTarget: string;
    AttributeName: string;
    AttributeLevel: string;
    CohortLabel?: string;
    LibraryRuleMetadata?: RuleEditor;
};

type Errors = {
    [key: string]: string;
};

interface IRuleFormProps {
    onSuccessfulSubmission: (options: {
        ruleFormValues: RuleFormValues,
        navigateToAndClause: boolean,
    }) => void;
    onNavigateToNewAndClause: () => void;
    currentIterationRule: IterationRule | undefined;
    selectedLibraryRule: LibraryRuleMetadata | undefined;
    addCustomRule?: boolean;
    existingRules: IterationRule[];
    ruleTypeFromRoute: RuleType;
    iterationCohorts: IterationCohort[];
    andClauseNumber?: number;
    withCohortLabel?: boolean;
    allCohorts?: IterationCohort[];
    teamDefaultRules?: IterationRule[];
    isSubmitting?: boolean;
    editMode?: boolean;
}

const generateRuleNameHintAndDescription = (ruleType: RuleType): {ruleNameHint: string, ruleDescription: string} => {
  if (ruleType === 'F') {
    return {
      ruleNameHint: "Choose a name that describes the filter rule and is easy to understand. For example 'NoUnder5 - Exclude Too Young'. Once saved, this description will be displayed in the summary report, which shows how many people have been excluded.",
      ruleDescription: "The rule description should be different from the rule name. For example 'Exclude anyone under 5 on the day'.",
    };
  }
  if (ruleType === 'S') {
    return {
      ruleNameHint: "Choose a name that describes the suppression rule and is easy to understand. You should refer to the group of people who should not be sent an invitation. For example 'CareHome – Suppress all Care Home Residents'. Once saved, this description will be displayed in the summary report, which shows how many people have been excluded.",
      ruleDescription: "The rule description should be different from the rule name. For example 'Exclude anyone living in a care home'.",
    };
  }
  if (ruleType === 'R') {
    return {
      ruleNameHint: "Choose a name that describes the routing rule and is easy to understand. For example, 'UnderAged16Template'.",
      ruleDescription: "The rule description should be different from the rule name. For example, 'Use under 16 years of age template'.",
    };
  }
  return {
    ruleNameHint: 'Choose a name that describes the rule and is easy to understand.',
    ruleDescription: 'The rule description should be different from the rule name.',
  };
};

export function RuleForm({
  onSuccessfulSubmission,
  onNavigateToNewAndClause,
  currentIterationRule,
  selectedLibraryRule,
  addCustomRule,
  existingRules,
  ruleTypeFromRoute,
  iterationCohorts,
  andClauseNumber,
  withCohortLabel = true,
  allCohorts = [],
  teamDefaultRules = [],
  isSubmitting = false,
  editMode = false,
}: IRuleFormProps) {
  const { campaign } = useCampaign();

  const libraryRuleMetadata = selectedLibraryRule?.LibraryRuleMetadata ?? currentIterationRule?.LibraryRuleMetadata;
  const ruleEditor = addCustomRule ? undefined : libraryRuleMetadata;
  const editorType = ruleEditor?.Type ?? 'DEFAULT';

  const { teamId, campaignId, iterationId } = useParams() as IIterationLevelParams;
  const { getCampaignPathFor } = useCampaignNavigate();
  const ruleTypePathSegment = buildRulePathParam(ruleTypeFromRoute);
  const baseRulePath = getCampaignPathFor(`iteration/${iterationId}/${ruleTypePathSegment}`);
  const changeLibraryRulePath = buildChangeLibraryRulePath(
    baseRulePath,
    currentIterationRule?.ID,
    andClauseNumber,
  );

  const { referenceData } = useReferenceData();
  const cohortsData = referenceData.cohorts;

  const operatorsData = referenceData.operators;
  const attributesData = referenceData.attributes;
  const { lockIterationIfRequired, error: lockError } = useIterationLock({ teamId, campaignId, iterationId });
  const initialRenderRef = useRef(true);

  const [ruleState, dispatch] = useReducer(
    (state: IRuleState, action: RuleActions) => ruleReducer(
      state,
      action,
      attributesData,
      operatorsData,
      cohortsData,
    ),
    buildInitialRuleState(
      attributesData,
      operatorsData,
      selectedLibraryRule ?? currentIterationRule,
      campaign.CampaignConfig.Target
        ? campaign.CampaignConfig.Target
        : 'COVID',
    ),
  );
  const [errors, setErrors] = useState<Errors>({});
  useEffect(() => {
    dispatch({
      type: 'SET_INITIAL_STATE',
      payload: buildInitialRuleState(
        attributesData,
        operatorsData,
        selectedLibraryRule ?? currentIterationRule,
        campaign.CampaignConfig.Target
          ? campaign.CampaignConfig.Target
          : 'COVID',
      ),
    });
  }, [campaign.CampaignConfig.Target, currentIterationRule, attributesData, operatorsData, selectedLibraryRule]);

  function clearErrorsForField(fieldName: string) {
    if (errors[fieldName]) {
      const clearedErrors = { ...errors };
      delete clearedErrors[fieldName];
      setErrors(clearedErrors);
    }
  }

  const isComparatorRequired = (): boolean => ruleState.selectedOperator.OperatorType !== 'N';

  const setOperator = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch({ type: 'SET_OPERATOR', payload: e.target.value });

    // Remove the comparator value for operators which do not require one
    const operatorType = ruleState.availableOperators.find(
      (operator) => operator.Operator === e.target.value,
    );
    if (operatorType?.OperatorType === 'N') {
      dispatch({ type: 'SET_COMPARATOR', payload: '' });
    }
  };

  const onSubmit = (navigateToAndClause: boolean) => {
    const newErrors: Errors = {};

    if (editMode && navigateToAndClause) {
      onNavigateToNewAndClause();
      return;
    }

    if (!ruleState.name && !andClauseNumber) {
      newErrors.AttributeName = "Enter a rule name. For example 'Language templates or People under the age of 75 (16-74)'.";
    } else if (
      !andClauseNumber
              && !ruleState.name.match(validStringCharacters)
    ) {
      newErrors.AttributeName = validStringCharacterMessage;
    }

    if (!ruleState.description && !andClauseNumber) {
      newErrors.Description = "Enter a rule description. For example 'Check languages'.";
    } else if (
      !andClauseNumber
              && !ruleState.description.match(validStringCharacters)
    ) {
      newErrors.Description = validStringCharacterMessage;
    }

    if (!ruleState.selectedComparator && isComparatorRequired()) {
      newErrors.Comparator = getComparatorRequiredError(ruleEditor);
    } else if (/~.~/.test(ruleState.selectedComparator)) {
      newErrors.Comparator = `The comparator should be modified to replace the library token(s) ${ruleState.selectedComparator.replace(/[^~]*~(.)~/g, '~$1~ ')}`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const nextRulePriority = currentIterationRule?.Priority ?? getNextRulePriorityForType(existingRules, ruleTypeFromRoute, teamDefaultRules);

    onSuccessfulSubmission({
      ruleFormValues: {
        AttributeLevel: ruleState.selectedAttributeLevel,
        AttributeName: ruleState.selectedAttribute.AttributeName,
        AttributeTarget: ruleState.selectedAttribute.ConditionType,
        Comparator: normaliseCommaSeparatedListWithAttributeToIgnoreWhitelist(ruleState.selectedAttribute.AttributeName, ruleState.selectedComparator),
        Description: ruleState.description,
        Name: andClauseNumber
          ? incrementAndClauseName(ruleState.name, andClauseNumber)
          : ruleState.name,
        Operator: ruleState.selectedOperator.Operator,
        Priority: nextRulePriority,
        CohortLabel: ruleState.cohortLabel,
        LibraryRuleMetadata: ruleEditor,
      },
      navigateToAndClause,
    });
    window.scrollTo(0, 0);
  };

  const handleTextChange = (
    e: ChangeEvent<HTMLInputElement>,
    actionType: ActionTypes,
  ) => {
    clearErrorsForField(e.target.name);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch({ type: actionType as any, payload: e.target.value });
  };

  const { ruleNameHint, ruleDescription } = generateRuleNameHintAndDescription(ruleTypeFromRoute);

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      lockIterationIfRequired();
    }
  }, [lockIterationIfRequired]);

  if (!attributesData || !operatorsData || !cohortsData) {
    return <p>Loading rule details...</p>;
  }

  const andClauseLabel = andClauseNumber ? "Add next 'AND' clause" : "Add 'AND' clause";

  return (
    <div className="rule-form">
      <Card>
        <>
          {editorType !== 'DEFAULT' && (
            <p className="nhsuk-heading-m">{selectedLibraryRule?.Name ?? ruleState.name}</p>
          )}
          <>
            <p>
              You can switch to a different rule. Any unsaved changes on this screen will be lost.
            </p>
            <Link
              to={{ pathname: changeLibraryRulePath }}
            >
              <Button secondary>Choose a different rule</Button>
            </Link>
          </>
          {!andClauseNumber && editorType === 'DEFAULT' && (
            <>
              <TextInput
                id="rule-attribute-name"
                label="Rule name"
                hint={ruleNameHint}
                name="AttributeName"
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleTextChange(e, 'SET_NAME')}
                value={ruleState.name}
                error={errors.AttributeName}
              />
              <TextInput
                id="rule-attribute-description"
                label="Rule description"
                hint={ruleDescription}
                name="Description"
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleTextChange(e, 'SET_DESCRIPTION')}
                value={ruleState.description}
                error={errors.Description}
              />
            </>
          )}

          {withCohortLabel && (
            <Select
              id="rule-cohort-label"
              label="Cohort"
              hint="Select a cohort to apply this rule only to that group. Select ‘Apply to all cohorts’ to apply this rule to all groups."
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                const selected = e.target.value;
                if (selected === NO_COHORTS_LABEL) {
                  dispatch({ type: 'SET_COHORT_LABEL', payload: undefined });
                  return;
                }

                dispatch({ type: 'SET_COHORT_LABEL', payload: selected });
              }}
              value={ruleState.cohortLabel}
              disabled={iterationCohorts.length === 0 && allCohorts.length === 0}
            >
              <Select.Option key="no-selection" value={undefined}>
                {NO_COHORTS_LABEL}
              </Select.Option>

              {iterationCohorts.length === 0 && (
              <Select.Option disabled>You have not selected any cohorts</Select.Option>
              )}
              {iterationCohorts
                .map((cohort) => cohort.CohortLabel)
                .filter((label) => {
                  const cohortStatus = getCohortStatus(label as string, cohortsData);
                  return cohortStatus === 'ok' || cohortStatus === 'provisional';
                })
                .map((label) => {
                  const cohortStatus = getCohortStatus(label as string, cohortsData);

                  return (
                    <Select.Option key={label} value={label}>
                      {label}
                      {cohortStatus === 'ok' ? '' : ` (${capitaliseFirstChar(cohortStatus)})`}
                    </Select.Option>
                  );
                })}
            </Select>
          )}

          {editorType === 'DEFAULT' && (
            <Select
              id="rule-attribute-level"
              label="Attribute level"
              onChange={(e: ChangeEvent<HTMLSelectElement>) => dispatch({ type: 'SET_ATTRIBUTE_LEVEL', payload: { selectedAttributeLevel: e.target.value, defaultConditionType: campaign.CampaignConfig.Target ? campaign.CampaignConfig.Target : 'COVID' } })}
              value={ruleState.selectedAttributeLevel}
            >
              {getAttributeLevels(attributesData).map((levelShortcode) => (
                <Select.Option key={levelShortcode} value={levelShortcode}>
                  {attributeLevelToLabel(levelShortcode)}
                </Select.Option>
              ))}
            </Select>
          )}
          {editorType === 'DEFAULT' && (
            <Select
              id="rule-condition-type"
              label="Condition type"
              onChange={(e: ChangeEvent<HTMLSelectElement>) => dispatch({ type: 'SET_CONDITION_TYPE', payload: e.target.value })}
              value={ruleState.selectedConditionType}
              disabled={!attributesHaveConditionType(attributesData, ruleState.selectedAttributeLevel)}
            >
              {ruleState.availableConditionTypes.map((conditionType) => (
                <Select.Option key={conditionType} value={conditionType}>
                  {conditionTargetToLabel(conditionType)}
                </Select.Option>
              ))}
            </Select>
          )}
          <Container>
            <EditorSelector
              ruleEditor={ruleEditor}
              ruleState={ruleState}
              errors={errors}
              dispatch={dispatch}
              deriveDisplayName={deriveDisplayName}
              setOperator={setOperator}
              handleTextChange={handleTextChange}
              isComparatorRequired={isComparatorRequired}
              teamCohorts={cohortsData}
            />

            {editorType === 'DEFAULT' && (
              <p>
                {ruleState.selectedAttribute.Description}
              </p>
            )}
            {editorType === 'DEFAULT' && ruleState.selectedAttribute.ValueSummary !== '' && ruleState.selectedAttribute.ValueSummary !== 'VALSUM' && (
              <p>
                Example values:&nbsp;
                {Array.isArray(ruleState.selectedAttribute.ValueSummary)
                  ? ruleState.selectedAttribute.ValueSummary.join(',')
                  : ruleState.selectedAttribute.ValueSummary}
              </p>
            )}
          </Container>
        </>
      </Card>
      <ApiErrorDetails apiError={lockError} />
      <Button className="nhsuk-u-margin-right-4" onClick={() => onSubmit(false)} disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Save and return to summary'}
      </Button>
      <Button onClick={() => onSubmit(true)} className="nhsuk-button--secondary-solid nhsuk-u-margin-right-4" disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : andClauseLabel}
      </Button>
    </div>
  );
}

export default RuleForm;
